import { useRef, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { FormFieldLegend } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

interface CheckboxGroupInputProps extends BaseFormFieldProps {
  options: { label: string; value: string; disabled?: boolean }[];
  name: string;
  defaultValue?: string[];
  value?: string[];
  onChange?: (values: string[]) => void;
  className?: string;
  layout?: 'vertical' | 'grid';
}

function CheckboxGroupInput({
  className,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  id,
  showAsterisk,
  errorText,
  invalid,
  options,
  name,
  defaultValue = [],
  value: controlledValue,
  onChange,
  layout = 'vertical',
}: CheckboxGroupInputProps) {
  const {
    fieldId: groupId,
    helperId,
    errorId,
    describedBy,
  } = useFormField(id, helperText, errorText);
  const isInvalid = Boolean(invalid || errorText);
  const isControlled = controlledValue !== undefined;
  const [uncontrolledValues, setUncontrolledValues] = useState<string[]>(defaultValue);
  const hiddenInputsRef = useRef<Map<string, HTMLInputElement>>(new Map());

  // Use controlled value if provided, otherwise use internal state
  const selectedValues = isControlled ? controlledValue : uncontrolledValues;

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const nextValues = checked
      ? [...selectedValues, value]
      : selectedValues.filter((v: string) => v !== value);

    if (isControlled) {
      // Controlled mode: call onChange callback
      onChange?.(nextValues);
    } else {
      // Uncontrolled mode: update internal state
      setUncontrolledValues(nextValues);
    }

    // Trigger change events on hidden inputs to notify Inertia Form
    // This ensures Inertia Form's isDirty tracking detects the changes
    requestAnimationFrame(() => {
      hiddenInputsRef.current.forEach((input) => {
        // Only trigger if this input's value is in the new selected values
        if (nextValues.includes(input.value)) {
          const changeEvent = new Event('change', { bubbles: true });
          input.dispatchEvent(changeEvent);
        }
      });
    });
  };

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      <fieldset aria-invalid={isInvalid || undefined} aria-describedby={describedBy} id={groupId}>
        <div className="grid gap-1.5">
          {label && <FormFieldLegend label={label} showAsterisk={showAsterisk} />}
          {helperText && helperTextPlacement === 'beforeControl' && (
            <FormFieldMessages helperText={helperText} helperId={helperId} />
          )}
        </div>
        {selectedValues.map((value: string) => (
          <input
            key={value}
            ref={(el) => {
              if (el) {
                hiddenInputsRef.current.set(value, el);
              } else {
                hiddenInputsRef.current.delete(value);
              }
            }}
            type="hidden"
            name={`${name}[]`}
            value={value}
          />
        ))}
        <div
          className={cn(
            'gap-3 mt-2.5',
            layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2' : 'flex flex-col',
          )}
        >
          {options.map((option) => {
            const optionId = `${groupId}-${option.value}`;
            const isChecked = selectedValues.includes(option.value);

            return (
              <div
                key={option.value}
                className={cn(
                  'flex items-center space-x-2',
                  option.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <Checkbox
                  id={optionId}
                  aria-label={option.label}
                  checked={isChecked}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(option.value, checked === true)
                  }
                  disabled={option.disabled}
                  aria-invalid={isInvalid || undefined}
                />
                <Label htmlFor={optionId} className="cursor-pointer font-normal">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
      </fieldset>
      {helperText && helperTextPlacement === 'afterControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </div>
  );
}

export { CheckboxGroupInput };
